import React from 'react';
import { PublicNav, Footer } from '../../components/PublicChrome';
import { SectionTitle, Panel } from '../../components/Primitives';
import { MarketsView } from '../../components/MarketsView';
import { useFetch } from '../../lib/hooks';
import { useI18n } from '../../i18n/I18nContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Bot, ShieldCheck, Activity, Layers, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';

function Wrap({ children }) {
  return (
    <div className="min-h-screen bg-vj-obsidian text-vj-silver">
      <PublicNav />
      <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-36 pb-10">{children}</div>
      <Footer />
    </div>
  );
}

export function PublicMarkets() {
  const { t } = useI18n();
  return <Wrap><SectionTitle eyebrow="Live Data" title={t('section.liveMarkets')} desc="Top-10 cryptocurrencies ranked by real market capitalization via CoinGecko, with candlestick charts and 24h statistics." /><MarketsView /></Wrap>;
}

export function HowItWorks() {
  const { t } = useI18n();
  const steps = [t('steps.1'), t('steps.2'), t('steps.3'), t('steps.4'), t('steps.5')];
  return (
    <Wrap>
      <SectionTitle center eyebrow="Process" title={t('section.howItWorks')} desc="From registration to withdrawal — fully transparent and auditable." />
      <div className="grid md:grid-cols-5 gap-4 mb-12">
        {steps.map((s, i) => (
          <div key={i} className="vj-card p-5">
            <div className="w-9 h-9 rounded-lg bg-vj-neon/10 text-vj-neon font-display font-bold grid place-items-center mb-4">{i + 1}</div>
            <p className="text-sm text-vj-silver/90">{s}</p>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <Panel title="Deposits — USDT BEP20"><p className="text-sm text-vj-grey">Send verified USDT on the BNB Smart Chain (chain ID 56) to the VEYJEX deposit address, then submit your transaction hash. We verify the transfer on-chain via BscScan, wait for the required confirmations, attribute it to your account, and record it in the authoritative ledger.</p></Panel>
        <Panel title="Withdrawals"><p className="text-sm text-vj-grey">Add and verify a BEP20 withdrawal address, then request a withdrawal from your withdrawable balance. Requests pass eligibility and limit checks, go through an approval workflow, and are marked completed only after a verified on-chain transfer.</p></Panel>
      </div>
    </Wrap>
  );
}

export function SIBotPublic() {
  const { t } = useI18n();
  const items = [
    { icon: Activity, t: 'Market Monitoring', d: 'Continuously ingests live market data and evaluates configured strategies.' },
    { icon: Bot, t: 'Transparent Status', d: 'Reports Offline, Monitoring, Strategy Active, Paused, Risk Limit Reached and more.' },
    { icon: ShieldCheck, t: 'Risk Controls', d: 'Exposure caps, daily loss limits, slippage limits and emergency stop.' },
    { icon: Layers, t: 'Execution Modes', d: 'Market monitoring, paper trading, live exchange trading, and admin-recorded data — always clearly labelled.' },
  ];
  return (
    <Wrap>
      <SectionTitle eyebrow={t('section.sibot')} title="The S.I. Bot" desc="A sophisticated, transparent trading workspace. It never claims predictive certainty, proprietary superintelligence, or guaranteed profits." />
      <div className="grid md:grid-cols-2 gap-6">
        {items.map((f) => (
          <Panel key={f.t}><div className="flex gap-4"><div className="w-11 h-11 rounded-xl bg-vj-neon/10 grid place-items-center shrink-0"><f.icon size={20} className="text-vj-neon" /></div><div><h3 className="font-display font-semibold mb-1">{f.t}</h3><p className="text-sm text-vj-grey">{f.d}</p></div></div></Panel>
        ))}
      </div>
    </Wrap>
  );
}

export function FAQPage() {
  const { t } = useI18n();
  const { data: faq } = useFetch('/faq');
  return (
    <Wrap>
      <SectionTitle eyebrow="Help" title={t('section.faq')} />
      <div className="max-w-3xl">
        <Accordion type="single" collapsible>
          {(faq || []).map((f) => (
            <AccordionItem key={f.id} value={f.id}>
              <AccordionTrigger className="text-left">{f.question}</AccordionTrigger>
              <AccordionContent className="text-vj-grey">{f.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Wrap>
  );
}

export function PublicSupport() {
  return (
    <Wrap>
      <SectionTitle eyebrow="Support" title="We're here to help" desc="Reach the VEYJEX team for account, deposit, and withdrawal questions." />
      <div className="grid md:grid-cols-3 gap-6">
        {[['Account & Security', 'Login, passkeys, and account recovery.'], ['Deposits & Withdrawals', 'On-chain verification and payout questions.'], ['Trading & S.I. Bot', 'Allocation, settlements, and bot status.']].map(([tt, dd]) => (
          <Panel key={tt}><h3 className="font-display font-semibold mb-1">{tt}</h3><p className="text-sm text-vj-grey">{dd}</p></Panel>
        ))}
      </div>
      <p className="text-sm text-vj-grey mt-8">Log in to submit a support request from your dashboard.</p>
    </Wrap>
  );
}

const LEGAL = {
  terms: { title: 'Terms of Service', body: 'These Terms govern your use of the VEYJEX platform. This is draft content pending professional legal review before launch. By using VEYJEX you accept the risks of cryptocurrency trading, including the potential total loss of funds. VEYJEX does not provide guaranteed returns and does not offer regulated financial advice.' },
  privacy: { title: 'Privacy Policy', body: 'This Privacy Policy describes how VEYJEX collects, uses, and protects personal data including account details and transaction records. We never request seed phrases or private keys. Draft content pending professional review.' },
  'risk-disclosure': { title: 'Risk Disclosure', body: 'Trading cryptocurrency involves substantial risk of loss and is not suitable for every investor. Prices are volatile. Paper-trading and admin-recorded results are never presented as real executed exchange trades. Only deposit funds you can afford to lose. Draft content pending professional review.' },
};

export function Legal({ kind }) {
  const page = LEGAL[kind] || LEGAL.terms;
  return (
    <Wrap>
      <div className="max-w-3xl">
        <SectionTitle eyebrow="Legal" title={page.title} />
        <div className="vj-card p-6"><p className="text-sm text-vj-grey leading-relaxed">{page.body}</p></div>
      </div>
    </Wrap>
  );
}
